import type { Logger } from '@amp/web-apps-logger';
import type { MTPerfkit } from '@amp-metrics/mt-perfkit';

import type {
    IPageSpeedMetric,
    PageData,
    PageDataEmpty,
    RenderMetrics,
} from './types';
import { getTimestamp } from './utils';

export class PageSpeedMetric implements IPageSpeedMetric {
    private readonly perfkit: Promise<MTPerfkit | null>;
    private readonly logger: Logger;
    private pageData: PageData | PageDataEmpty;
    private renderMetrics: RenderMetrics;
    private readonly deleteInstance: () => void;
    constructor(
        perfkit: Promise<MTPerfkit | null>,
        logger: Logger,
        deleteInstance: () => void,
    ) {
        this.perfkit = perfkit;
        this.logger = logger;
        this.pageData = {
            pageId: null,
            pageContext: null,
            pageType: null,
            isFirstPage: null,
        };
        this.deleteInstance = deleteInstance;
        this.renderMetrics = {
            pageAppearTime: null,
            pageRequestTime: null,
            pageAfterFirstRenderTime: null,
            pageEndTime: null,
        };
    }
    private isPageDataEmpty(): boolean {
        return Object.values(this.pageData).every((value) => value === null);
    }
    public capturePageRequestTime(): void {
        if (this.renderMetrics.pageRequestTime === null) {
            this.renderMetrics.pageRequestTime = getTimestamp();
            this.logger.debug('pageRequestTime metric', this.renderMetrics);
            return;
        }
        this.logger.debug('request time has already been captured');
    }

    public capturePageData(
        pageData: PageData,
        config: { capturePageEndTime?: boolean } = {},
    ): void {
        const shouldCaptureEndTime = config.capturePageEndTime || false;
        if (this.isPageDataEmpty()) {
            this.pageData = pageData;
            this.logger.debug('pageData captured', this.pageData);

            if (shouldCaptureEndTime) {
                this.capturePageEndTime();
            }
            return;
        }
        this.logger.debug(
            'pageData has already been set. Create a new metric to track other pages',
        );
    }
    public capturePageAppearTime(): void {
        if (this.renderMetrics.pageAppearTime === null) {
            this.renderMetrics.pageAppearTime = getTimestamp();
            this.logger.debug('pageAppearTime metric', this.renderMetrics);
            return;
        }
        this.logger.debug('appear time has already been captured');
    }
    public capturePageEndTime(): void {
        if (this.isPageDataEmpty()) {
            this.logger.warn(
                'pageData is empty. render metrics are not captured',
            );
            this.deleteInstance();
            return;
        }

        // Uses double rAF to guarantee that we record *after* paint.
        // In older versions of Safari, the rAF callback runs too early,
        // before Paint, so a double rAF was necessary to guarantee that it
        // would run after paint. Other browsers should not be affected by this.
        // https://github.pie.apple.com/amp-ui/ember-metrics/blob/main/addon/services/perfkit.js#L276-L279
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (this.renderMetrics.pageRequestTime) {
                    this.renderMetrics.pageAfterFirstRenderTime =
                        getTimestamp();
                    this.renderMetrics.pageEndTime = getTimestamp();
                    this.logger.debug('PageEndTime metric', this.renderMetrics);

                    this.logger.debug('recording metric', {
                        renderMetrics: this.renderMetrics,
                        pageData: this.pageData,
                    });
                    this.recordPageRenderMetric();
                } else {
                    this.logger.warn(
                        'Invalid metrics, no request time was captured',
                    );
                    this.deleteInstance();
                }
            });
        });
    }

    private recordPageRenderMetric(): void {
        this.perfkit
            .then((perfKit) => {
                if (!perfKit) {
                    return;
                }

                const { pageContext, pageId, pageType, isFirstPage } = this
                    .pageData as PageData;
                const pageRender = perfKit.eventHandlers.pageRender.instance(
                    pageId,
                    pageType,
                    pageContext,
                );

                pageRender.addData(this.renderMetrics);
                pageRender.setIsAppLaunch(isFirstPage);
                pageRender.recordEvent(); // Records via mt-perfkit eventRecorder
            })
            .catch((e) => {
                const { pageContext, pageId, pageType } = this.pageData;
                this.logger.error(
                    `render metrics failed for: ${pageContext}, ${pageId}, ${pageType}`,
                    e,
                );
            })
            .finally(() => this.deleteInstance());
    }
}
